/**
 * Dispatcher that turns an `ExceptionFilterStripDef` into the matching
 * concrete strip component (cards / tabs) and handles URL writes
 * uniformly. `DataViewFilterPanel` renders one of these per declared
 * strip in the appropriate zone (cards in the top, tabs in the footer);
 * the switch on `strip.type` lives here so the panel stays agnostic to
 * the union.
 *
 * Page resets: any URL write also clears `?page=` so a new filter does
 * not leave the user stranded on an offset that no longer makes sense.
 *
 * Outer chrome (the Paper border / padding) lives on `DataViewFilterPanel`
 * - this dispatcher returns the bare strip content only.
 */
import React from 'react'
import IssueCardStrip from './IssueCardStrip'
import ExceptionFilterTabs from './ExceptionFilterTabs'
import type { ExceptionFilterStripDef } from '../../../config/exceptions/types'

interface FilterStripRendererProps {
  strip: ExceptionFilterStripDef
  response: Record<string, any> | null
  filterCounts: Record<string, number> | null
  searchParams: URLSearchParams
  setSearchParams: (next: URLSearchParams, opts?: { replace?: boolean }) => void
}

const FilterStripRenderer: React.FC<FilterStripRendererProps> = ({
  strip,
  response,
  filterCounts,
  searchParams,
  setSearchParams,
}) => {
  if (strip.type === 'cards') {
    const cards = strip.buildCards(response, filterCounts)
    if (cards.length === 0) return null
    const activeKey = strip.getActiveKey(searchParams)
    const handleSelect = (key: string | null) => {
      const next = strip.applySelection(searchParams, key)
      // Skip the URL write when applySelection didn't actually mutate
      // the params (e.g. the sibling-codes strip navigates internally
      // and returns the original params unchanged). Avoids a stray
      // history entry and prevents racing the in-flight navigate().
      if (next.toString() === searchParams.toString()) return
      next.delete('page')
      setSearchParams(next, { replace: false })
    }
    return (
      <IssueCardStrip
        cards={cards}
        activeKey={activeKey}
        onSelect={handleSelect}
        isCardActive={strip.isCardActive}
      />
    )
  }

  // tabs
  const raw = searchParams.get(strip.paramKey) ?? strip.defaultValue
  const validValues = new Set(strip.tabs.map((t) => t.value))
  const activeValue = validValues.has(raw) ? raw : strip.defaultValue
  const handleChange = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value === strip.defaultValue) next.delete(strip.paramKey)
    else next.set(strip.paramKey, value)
    next.delete('page')
    setSearchParams(next, { replace: false })
  }
  return (
    <ExceptionFilterTabs
      paramKey={strip.paramKey}
      tabs={strip.tabs}
      activeValue={activeValue}
      response={response}
      onChange={handleChange}
    />
  )
}

export default FilterStripRenderer
